/**
 * Input Component - AWLP Design System
 * Form input with label, validation states, icons, and helper text
 */

import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';
import styled, { css } from 'styled-components';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

const Container = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const InputWrapper = styled.div<{ $hasError: boolean; $disabled: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  min-height: 44px; /* WCAG compliant touch target */
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: ${({ theme }) => theme.transitions.fast};

  ${({ $hasError, theme }) =>
    $hasError &&
    css`
      border-color: ${theme.colors.status.error.text};     
    `}

  ${({ $disabled, theme }) =>
    $disabled &&
    css`
      background: ${theme.colors.backgrounds.light};
      cursor: not-allowed;
      opacity: 0.6;
    `}

  &:hover:not(:focus-within) {
    border-color: ${({ theme, $hasError }) =>
    $hasError ? theme.colors.status.error.text : theme.colors.borders.medium};
  }

  &:focus-within {
    border-color: ${({ theme, $hasError }) =>
    $hasError ? theme.colors.status.error.text : theme.colors.primary.base};
    box-shadow: ${({ theme, $hasError }) =>
    $hasError ? theme.shadows.ringError : theme.shadows.ring};
  }
`;

const StyledInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: none;
  background: transparent;
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.primary};
  outline: none;
  box-shadow: none;
  border-radius: inherit;

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.light};
  }

  &:disabled {
    cursor: not-allowed;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 30px ${({ theme }) => theme.colors.backgrounds.white} inset;
    -webkit-text-fill-color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const IconWrapper = styled.span<{ $position: 'left' | 'right' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: 0 ${({ theme }) => theme.spacing[3]};

  ${({ $position }) =>
    $position === 'left' &&
    css`
      padding-right: 0;
    `}

  ${({ $position }) =>
    $position === 'right' &&
    css`
      padding-left: 0;
    `}

  svg {
    width: 18px;
    height: 18px;
  }
`;

const HelperText = styled.span<{ $isError: boolean }>`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme, $isError }) =>
    $isError ? theme.colors.status.error.text : theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
`;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled = false,
      ...props
    },
    ref,
  ) => {
    const hasError = Boolean(error);

    return (
      <Container $fullWidth={fullWidth}>
        {label && <Label>{label}</Label>}
        <InputWrapper $hasError={hasError} $disabled={disabled}>
          {leftIcon && <IconWrapper $position="left">{leftIcon}</IconWrapper>}
          <StyledInput ref={ref} disabled={disabled} {...props} />
          {rightIcon && <IconWrapper $position="right">{rightIcon}</IconWrapper>}
        </InputWrapper>
        {(error || helperText) && (
          <HelperText $isError={hasError}>{error || helperText}</HelperText>
        )}
      </Container>
    );
  },
);

Input.displayName = 'Input';

export default Input;
